'use client';

import React, { useEffect, useRef } from 'react';

const marketData = [
  { label: 'TAM', value: '$990M+', desc: 'Total Addressable Market', sub: 'Kenya egg market at full demand', color: '#FACC15' },
  { label: 'SAM', value: '$650M', desc: 'Serviceable Addressable Market', sub: 'Accessible via distribution infrastructure', color: '#22C55E' },
  { label: 'SOM', value: '$19.5M', desc: 'Serviceable Obtainable Market', sub: 'Year 3 target revenue capture', color: '#16A34A' },
];

const competitiveRows = [
  { feature: 'Industrial-scale automation', batis: true, smallFarm: false, imports: false },
  { feature: 'Climate-controlled housing', batis: true, smallFarm: false, imports: false },
  { feature: 'Disease-resistant breeds (Lohmann)', batis: true, smallFarm: false, imports: true },
  { feature: 'Multi-revenue streams', batis: true, smallFarm: false, imports: false },
  { feature: 'Sustainable waste-to-value', batis: true, smallFarm: false, imports: false },
  { feature: 'European-grade equipment', batis: true, smallFarm: false, imports: true },
  { feature: 'Domestic supply chain control', batis: true, smallFarm: true, imports: false },
  { feature: 'Cost-competitive at scale', batis: true, smallFarm: false, imports: false },
];

const CheckIcon = ({ yes }: { yes: boolean }) => (
  yes ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-label="Yes" role="img">
      <circle cx="12" cy="12" r="10" fill="rgba(0, 255, 94, 0.15)" />
      <path d="M8 12l3 3 5-5" stroke="#00ff5e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-label="No" role="img">
      <circle cx="12" cy="12" r="10" fill="rgba(255, 9, 9, 0.15)" />
      <path d="M15 9l-6 6M9 9l6 6" stroke="rgb(255, 0, 0)" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
);

export default function MarketSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -60px 0px' }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="market"
      ref={sectionRef}
      className="py-32 lg:py-40"
      style={{ background: 'linear-gradient(180deg, #061409 0%, #030D06 100%)' }}
      aria-labelledby="market-heading"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Label */}
        <div className="scroll-reveal mb-6">
          <span className="section-label">03 / Market Opportunity</span>
        </div>

        {/* Headline */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          <div className="scroll-reveal">
            <h2 id="market-heading" className="font-display text-[clamp(2.5rem,5.5vw,4.5rem)] leading-[0.9] tracking-[-0.04em] text-white">
              A $990M market<br />with structural<br />
              <span className="text-yellow-400">demand guarantees.</span>
            </h2>
          </div>
          <div className="scroll-reveal flex items-end" style={{ transitionDelay: '100ms' }}>
            <p className="text-lg text-white/70 leading-relaxed">
              Kenya&apos;s egg market grows at 10% annually with consumption still at 20% of recommended levels. Entry timing is not a question of market viability — it is a question of execution capacity.
            </p>
          </div>
        </div>

        {/* TAM/SAM/SOM — Asymmetric bento */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16 stagger-children">
          {marketData.map((item, idx) => (
            <div
              key={item.label}
              className="scroll-reveal glass-dark rounded-2xl p-8 hover-lift"
              style={{ transitionDelay: `${idx * 120}ms` }}
            >
              <div className="flex items-start justify-between mb-6">
                <span
                  className="text-xs font-bold font-mono-custom px-3 py-1 rounded-full"
                  style={{ background: `${item.color}18`, color: item.color, border: `1px solid ${item.color}30` }}
                >
                  {item.label}
                </span>
              </div>
              <div className="font-display text-[clamp(2rem,4vw,3rem)] leading-none tracking-[-0.05em] mb-3" style={{ color: item.color }}>
                {item.value}
              </div>
              <p className="text-sm font-semibold text-white/70 mb-1">{item.desc}</p>
              <p className="text-xs text-white/70">{item.sub}</p>
            </div>
          ))}
        </div>

        {/* Market signals grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-20 stagger-children">
          {[
            { value: '54M', label: 'Population served', sub: 'Kenya' },
            { value: '10%', label: 'Annual market growth', sub: 'Compound' },
            { value: '36', label: 'Current eggs/capita', sub: 'vs 180 recommended' },
            { value: '12mo', label: 'To full operations', sub: 'Post-funding' },
          ].map((stat, idx) => (
            <div
              key={stat.label}
              className="scroll-reveal glass-green rounded-xl p-5 text-center"
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              <div className="font-display text-3xl text-yellow-400 tracking-[-0.04em]">{stat.value}</div>
              <p className="text-xs text-white/70 mt-1 font-medium">{stat.label}</p>
              <p className="text-xs text-white/70 mt-0.5 font-mono-custom">{stat.sub}</p>
            </div>
          ))}
        </div>

        {/* Competitive advantage table */}
        <div className="scroll-reveal" style={{ transitionDelay: '300ms' }}>
          <div className="mb-8">
            <span className="section-label">Competitive Advantage</span>
            <h3 className="font-display text-3xl text-white mt-3 tracking-tight">
              Subscale competitors are structurally<br />
              <span className="text-white/70">incapable of meeting national demand.</span>
            </h3>
          </div>

          <div className="glass-dark rounded-2xl overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-4 gap-0 border-b border-white/5">
              <div className="p-5 text-xs text-white/70 font-mono-custom uppercase tracking-widest">Capability</div>
              <div className="p-5 text-center">
                <span className="text-sm font-bold text-yellow-400">Batis Poultry</span>
              </div>
              <div className="p-5 text-center">
                <span className="text-xs text-white/70 font-medium">Small Farms</span>
              </div>
              <div className="p-5 text-center">
                <span className="text-xs text-white/70 font-medium">Imports</span>
              </div>
            </div>

            {/* Table rows */}
            {competitiveRows.map((row, idx) => (
              <div
                key={row.feature}
                className={`grid grid-cols-4 gap-0 border-b border-white/5 last:border-0 transition-colors hover:bg-white/[0.02] ${idx % 2 === 0 ? '' : 'bg-white/[0.01]'}`}
              >
                <div className="p-4 text-sm text-white/70 flex items-center">{row.feature}</div>
                <div className="p-4 flex items-center justify-center"><CheckIcon yes={row.batis} /></div>
                <div className="p-4 flex items-center justify-center"><CheckIcon yes={row.smallFarm} /></div>
                <div className="p-4 flex items-center justify-center"><CheckIcon yes={row.imports} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}